import * as React from 'react';
import { FlatList, Platform, Pressable, Text, View, StyleSheet } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { AppContext } from "../context/AppContext";
import { Divider } from 'react-native-paper';
import { CustomScrollView } from '../components/CustomScrollView';
import { CustomButton } from '../components/CustomButton';
import { TextInput } from 'react-native-paper';
import { useForm, Controller } from "react-hook-form";
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete';
import Constants from 'expo-constants';
import { useSelector } from 'react-redux';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  wrapper: {
    flex: 1,
    padding: 20,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    paddingBottom: 10,
  },
  labelPadLeft: {
    fontSize: 14,
    fontWeight: "bold",
    paddingLeft: 5,
  },
  normal: {
    fontSize: 14,
    paddingRight: 10,
    flexShrink: 1,
  },
  divider: {
    marginVertical: 15,
  },
  line: {
    paddingBottom: 15,
    flexWrap: "wrap",
  },
  listItemStyle: {
    paddingLeft: 5,
  },
  itemWrapper: {
    padding: 10,
  }
});

export function ListLocationsNear({ navigation }) {
  const { showPermanentSnack, showTemporarySnack, hideSnack, getLoginState, showSpinner, hideSpinner } = React.useContext(AppContext);
  const dataFilter = useSelector((state) => state.dataFilter);
  const autocompleteRef = React.useRef();
  const [ results, setResults ] = React.useState([]);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      distance: 25,
      address: "",
    }
  });

  async function onSubmit(data) {
    try {
      showSpinner();
      hideSnack();

      const pieces = data.address.split(",");

      let distanceMiles = parseFloat(data.distance);
      const search = Object.assign({}, dataFilter, {
        lat: pieces[0],
        lng: pieces[1],
        incp: "y",
        incl: "y",
        dmi: distanceMiles,
      });

      const response = await AppUtilities.fetchUnauthorized(
        "ListLocationsNear.onSubmit",
        AppUtilities.getMainDataLink(search),
        "GET",
      );
      if (response.body) {
        const searchResults = [];
        for (const searchResult of response.body.locations) {
          searchResults.push({
            location: {
              id: searchResult[0],
              name: response.body.names[searchResult[3]],
            },
            products: response.body.locationProducts[searchResult[0]],
          });
        }
        setResults(searchResults);
        hideSpinner();
      } else {
        throw new Error("Result body not found");
      }
    } catch (e) {
      showPermanentSnack(AppUtilities.getErrors(e), "Retry", () => onSubmit(data));
      AppUtilities.handleException(e, "ListLocationsNear.onSubmit");
    } finally {
      hideSpinner();
    }
  }

  async function onValidationErrors(errors) {
    showTemporarySnack("There are some errors in the form");
  }

  function renderItem(data) {
    return (
      <Pressable style={styles.itemWrapper} onPress={() => pressItem(data)}>
        <Text>{data.item.location.name}: {data.item.products.map((product) => product.product_name).join(", ")}</Text>
      </Pressable>
    );
  }

  function pressItem(data) {
    navigation.navigate(AppConstants.SCREENS.LOCATION_LIST_DETAILS, { locationid: data.item.location.id });
  }

  return (
    <View style={styles.container}>
      <FlatList
        keyboardShouldPersistTaps="handled"
        data={results}
        renderItem={renderItem}
        ItemSeparatorComponent={Divider}
        ListHeaderComponent={(
          <>
            <Text selectable={true} style={styles.normal}>Enter a location and press Submit to list locations and products near there:</Text>
            <Divider style={AppConstants.STYLES.marginVertical10} />

            {errors.address && <Text selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>Location is required.</Text>}
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <View style={{ minHeight: 75, }}>
                  <GooglePlacesAutocomplete
                    placeholder="Location"
                    minLength={2}
                    debounce={200}
                    autoFocus={false}
                    ref={autocompleteRef}
                    //listViewDisplayed="auto"
                    fetchDetails={true}
                    predefinedPlaces={[]}
                    styles={{
                      container: {
                        //margin: 10,
                      },
                      description: {
                        fontWeight: "bold",
                        //color: "black",
                      },
                      listView: {
                      },
                      predefinedPlacesDescription: {
                        //color: "black",
                      },
                      textInput: {
                        backgroundColor: "#f6f6f6",
                      },
                      textInputContainer: {
                        width: "100%",
                        backgroundColor: "#f6f6f6",
                        borderWidth: 1,
                        borderColor: "#717171",
                        //borderRadius: 5,
                      },
                      row: {
                        //backgroundColor: "transparent",
                      }
                    }}
                    textInputProps={{ placeholderTextColor: "#717171" }}
                    query={{
                      key: Constants.expoConfig.extra.googleNoRestrictionsKey,
                      language: "en",
                      //types: "(cities)",
                    }}
                    onPress={(data, details) => {
                      onChange(details.geometry.location.lat + "," + details.geometry.location.lng);
                    }}
                    GooglePlacesDetailsQuery={{
                      fields: "formatted_address,geometry",
                    }}
                    enablePoweredByContainer={false}
                    timeout={10000} //ms
                    onFail={(error) => {
                      showTemporarySnack("Failed lookup: " + error);
                    }}
                    onTimeout={(data) => {
                      showTemporarySnack("Timed out. Check your internet connection.");
                    }}
                    requestUrl={{
                      useOnPlatform: 'web',
                      url: AppUtilities.getRootUri() + 'proxy/https://maps.googleapis.com/maps/api',
                    }}
                  />
                </View>
              )}
              name="address"
            />

            {errors.distance && <Text selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>Error in distance.</Text>}
            <Controller
              control={control}
              rules={{
                required: false,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  label="Maximum distance (miles)"
                  value={value}
                  onChangeText={onChange}
                  //style={AppConstants.STYLES.width100PmarginVertical10borderNone}
                  mode="outlined"
                  dense={true}
                  returnKeyType="next"
                  onBlur={onBlur}
                  keyboardType="decimal-pad"
                  inputMode="decimal"
                />
              )}
              name="distance"
            />

            <CustomButton
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              style={AppConstants.STYLES.marginVertical10}
            >Submit</CustomButton>
          </>
        )}
        ListFooterComponent={(
          <>
            <Divider style={AppConstants.STYLES.marginTop10} />
          </>
        )}
      />
    </View>
  );
}
